import { Dispatch, SetStateAction, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useDatasetContext } from '../../../hooks/context/useDatasetContext';
import { getSubsetAndItemParams } from '../../../utils/getSubsetAndItemParams';
import { GlobalLocationStateType } from '../../../types/types';

// Back to list from a form on a filterFirst dataset with no target row (e.g.
// externally-opened form): omitFilterFirstFilter skips the filter modal, so
// activeFilters stays null and nothing fetches. The locate guard is itemId-gated,
// so honor the flag here, otherwise the list loads forever.
export const useFilterFirstReturnFetchGuard = (
  datasetShown: boolean | undefined,
  setSearchForRowInProcess: Dispatch<SetStateAction<boolean>>
) => {
  const history = useHistory();
  const { pathname, state } = useLocation<GlobalLocationStateType>();
  const {
    activeFilters,
    activeSorting,
    datasetId,
    datasetData,
    dataHookLoadingDatasetId,
    fetchDatasetData,
  } = useDatasetContext();

  useEffect(() => {
    if (!state?.fetchDataOnFilterFirstDataset) return;

    const { itemId } = getSubsetAndItemParams(pathname);
    if (itemId || !datasetShown || dataHookLoadingDatasetId !== undefined)
      return;

    // A long-running poll started before the form was opened may have just
    // delivered this dataset's data (it survives entering a form — same data
    // scope). Reuse it: only consume the one-shot flag, do NOT fire a fresh
    // fetch that would discard the just-polled result. datasetData (raw hook
    // result) flips synchronously with the loading clear, so it is reliable
    // here even though context `data` lags one render behind.
    if (datasetData?.datasetId === datasetId) {
      history.replace(pathname, {
        ...state,
        fetchDataOnFilterFirstDataset: undefined,
      });
      return;
    }

    history.replace(pathname, {
      ...state,
      fetchDataOnFilterFirstDataset: undefined,
    });
    setSearchForRowInProcess(true);
    fetchDatasetData(
      undefined,
      undefined,
      activeSorting,
      activeFilters || undefined
    ).finally(() => setSearchForRowInProcess(false));
  }, [
    datasetShown,
    datasetId,
    datasetData,
    dataHookLoadingDatasetId,
    state?.fetchDataOnFilterFirstDataset,
  ]);
};
